import type { RemoteGeoJSONBlock } from "@/blocks/geojson.blocks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, MapPin, Edit3, Check, X, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useRoom } from "@/hooks/use-room";
import { GeoJSONSchema } from "zod-geojson";
import { gpx } from "@mapbox/togeojson";

export default function RemoteGeoJson({
  block,
}: {
  block: RemoteGeoJSONBlock;
}) {
  const { docs } = useRoom();
  const { url, cache } = block.attributes;
  const [isEditing, setIsEditing] = useState(false);
  const [editUrl, setEditUrl] = useState(url);
  const [urlError, setUrlError] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  // Extract features count from cache if available
  const featureCount = cache?.data?.features?.length;

  // Detect file type from URL
  const isGpxFile = url.toLowerCase().includes(".gpx");

  // Dataset name
  const datasetName =
    cache?.data?.name || `Remote ${isGpxFile ? "GPX" : "GeoJSON"} document`;

  let displayUrl;
  try {
    const parsedURL = new URL(url);
    displayUrl = `${parsedURL.hostname}${parsedURL.pathname}`;
  } catch {
    displayUrl = url;
  }

  const validateUrl = (urlString: string): boolean => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = () => {
    if (!validateUrl(editUrl)) {
      setUrlError("Please enter a valid URL");
      return;
    }

    if (!docs) return;
    const { cache: _, ...attributesWithoutCache } = block.attributes;
    docs.updateBlockAttributes(block.id, {
      ...attributesWithoutCache,
      url: editUrl,
    });

    setIsEditing(false);
    setUrlError("");
  };

  const handleFetch = async () => {
    if (!docs) return;

    setIsFetching(true);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let geojsonData;

      if (isGpxFile) {
        // Handle GPX files
        const textData = await response.text();
        const parser = new DOMParser();
        const gpxDoc = parser.parseFromString(textData, "application/xml");

        // Check for parsing errors
        const parserError = gpxDoc.querySelector("parsererror");
        if (parserError) {
          throw new Error("Invalid GPX file: XML parsing failed");
        }

        // Convert GPX to GeoJSON
        geojsonData = gpx(gpxDoc);
      } else {
        // Handle GeoJSON files
        geojsonData = await response.json();
      }

      const parsedData = GeoJSONSchema.safeParse(geojsonData);

      if (!parsedData.success) {
        throw new Error(`Invalid ${isGpxFile ? "GPX" : "GeoJSON"} data`);
      }

      // Update the cache with new data
      docs.updateBlockAttributes(block.id, {
        ...block.attributes,
        cache: {
          timestamp: Date.now(),
          data: geojsonData as any,
        },
      });
    } catch (error) {
      // Update cache with error information
      docs.updateBlockAttributes(block.id, {
        ...block.attributes,
        cache: {
          timestamp: Date.now(),
          data: null,
          error:
            error instanceof Error ? error.message : "Failed to fetch data",
        },
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleCancel = () => {
    setEditUrl(url);
    setIsEditing(false);
    setUrlError("");
  };

  const handleUrlChange = (value: string) => {
    setEditUrl(value);
    if (urlError) {
      setUrlError("");
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {datasetName}
          </div>
          {!isEditing && (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFetch}
                disabled={isFetching}
                className="h-7 w-7 p-0"
                title="Fetch data"
              >
                <RefreshCw
                  className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`}
                />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="h-7 w-7 p-0"
                title="Edit URL"
              >
                <Edit3 className="h-3 w-3" />
              </Button>
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isEditing ? (
          <div className="space-y-2">
            <Input
              value={editUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="Enter GeoJSON or GPX URL"
              className="text-sm"
              aria-invalid={!!urlError}
            />
            {urlError && (
              <div className="text-xs text-destructive">{urlError}</div>
            )}
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave}>
                <Check className="h-3 w-3" />
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancel}>
                <X className="h-3 w-3" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground truncate" title={url}>
            {displayUrl}
          </div>
        )}

        {!isEditing && cache?.error && (
          <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
            Error: {cache.error}
          </div>
        )}

        {!isEditing && featureCount !== undefined && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" />
            {featureCount} feature{featureCount !== 1 ? "s" : ""}
          </div>
        )}
        {!isEditing && cache && !cache.error && (
          <div className="text-xs text-muted-foreground">
            Last fetched on {new Date(cache.timestamp).toLocaleString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
